import {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useState,
} from 'react'
import useIsMounted from 'utilities/hooks/isMounted'
import { Select, Stack, TextField } from 'ui/baseComponents'
import IconButton from '@material-ui/core/IconButton'
import { validate } from 'email-validator'
import CloseIcon from '@material-ui/icons/Close'
import SubmitButton from '../../../components/SubmitButton/SubmitButton'
import OrgContext from '../../../contexts/OrgContext'
import { constructRoleVariables, useCreateMembership } from '../mutations'
import { GET_USERS } from 'domains/carrotGraph/queries'
import { UserPHIContext } from 'ui/contexts'

const roles = [
  {
    key: 'member',
    name: 'User',
    value: 'Member',
    'data-cy': 'invite-user-role-option-user',
  },
  {
    key: 'admin',
    name: 'Admin',
    value: 'Admin',
    'data-cy': 'invite-user-role-option-admin',
  },
  {
    key: 'clinician',
    name: 'Clinician',
    value: 'Clinician',
    'data-cy': 'invite-user-role-option-clinician',
  },
]

export const isValid = ({ displayName, email }) => {
  return validate(email) && displayName.length < 100
}

export const getDuplicateErrorMessage = (email) =>
  `A user with the email ${email} is already a member.`

export const InviteUserRow = forwardRef(
  ({ user = {}, setUser, onClose = () => {}, showCloseIcon }, ref) => {
    const phiVisibility = useContext(UserPHIContext)
    const { admin, email, displayName, roleDisplayName } = user
    const organization = useContext(OrgContext)
    const [error, setError] = useState(false)
    const [success, setSuccess] = useState(false)
    const [timeoutId, setTimeoutId] = useState(false)
    const isMounted = useIsMounted()

    const ROLE_OPTIONS = phiVisibility
      ? [...roles]
      : roles.filter((role) => role.value !== 'Clinician')

    const doClose = () => {
      if (!isMounted) return
      onClose()
    }
    if (success && !timeoutId) {
      setTimeoutId(setTimeout(doClose, 1500))
    }

    const [createMembership, { loading }] = useCreateMembership({
      onError: (error) => {
        console.error(error)
        const errorMessage = error.message.includes(409)
          ? getDuplicateErrorMessage(email)
          : `Unable to invite user ${email}`

        setError(errorMessage)
      },
      refetchQueries: [
        {
          query: GET_USERS,
          variables: { orgId: organization.id, withDisabled: true },
        },
      ],
    })
    // Expose submit to parent via a ref
    useImperativeHandle(ref, () => ({ submit: handleSubmit }))

    const handleSubmit = useCallback(
      (e) => {
        e && e.preventDefault()
        const membership = {
          admin,
          displayName,
          email,
          orgId: organization.id,
        }

        const updateCreateMembershipVariables = (roleDisplayName) => {
          const [adminVal, canSeePHIVal] =
            constructRoleVariables(roleDisplayName)
          membership['admin'] = adminVal
          membership['canSeePHI'] = canSeePHIVal
        }
        updateCreateMembershipVariables(roleDisplayName)

        if (!isValid(user)) return

        return createMembership({ variables: { input: membership } }).then(
          (result) => {
            // If mutation fails, result is undefined
            setSuccess(!!result)
          },
        )
      },
      [
        admin,
        createMembership,
        displayName,
        email,
        organization.id,
        user,
        roleDisplayName,
      ],
    )
    const handleChange = (attr) => (e) => {
      setError(false)
      setUser({ ...user, [attr]: e.target.value })
    }

    return (
      <form id="invite-user" onSubmit={handleSubmit} data-cy="InviteUserRow">
        <Stack direction="row" alignItems="flex-end" sx={{ margin: '.5rem 0' }}>
          <Stack direction="row" alignItems="flex-end" gap={2} width="95%">
            <TextField
              autoFocus
              error={!!error}
              helperText={error}
              id="email"
              data-cy="emailField"
              label="Email Address"
              type="email"
              value={email}
              onChange={handleChange('email')}
              variant="standard"
            />
            <TextField
              id="displayName"
              data-cy="displayName"
              label="Display Name"
              value={displayName}
              variant="standard"
              onChange={handleChange('displayName')}
            />
            <Select
              value={roleDisplayName}
              label="Type"
              onChange={handleChange('roleDisplayName')}
              options={ROLE_OPTIONS}
              showLabel={false}
              variant="standard"
              data-cy="userType"
            />
            <SubmitButton
              success={success}
              loading={loading}
              successLabel="Invited"
              submitLabel="Invite"
              disabled={!isValid(user) || loading}
            />
          </Stack>
          <Stack direction="row" alignItems="middle">
            {showCloseIcon && (
              <IconButton
                size="small"
                edge="start"
                aria-label="close"
                onClick={doClose}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </form>
    )
  },
)
export default InviteUserRow
